/**
 * Eric 2026-05-12 — Onglet Connaissances de VideoDetailPage (Refacto P2 phase 3).
 * Affiche les knowledge_units extraites de la vidéo avec icône colorée par type
 * et seek vers le timestamp au clic.
 *
 * Eric 2026-05-18 (Vincent #146/#147) :
 *   - #147 : Filtre out procedures (déjà visible dans l'onglet "Procédures").
 *   - #146 : Boutons Accepter / Éditer / Refuser pour chaque item pending_review
 *           (concepts, scripts, objections, Q&R orphelines). Édition ouvre le
 *           KnowledgeDetailModal partagé via la callback `onEditItem`.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "../../lib/uiDialogs";
import { Brain, Lightbulb, Check, X, PencilSimple, SpinnerGap } from "@phosphor-icons/react";
import { API_BASE as API } from "../../lib/apiBase";
import KUFavoriteHeart from "./KUFavoriteHeart";
import { useFavoriteKuIds } from "../../hooks/useFavoriteKuIds";

export default function KnowledgeTab({
  knowledgeUnits = [],
  isProcessing = false,
  typeIcons = {},
  typeColors = {},
  formatTime,
  seekTo,
  lang = "fr",
  noKnowledgeYetLabel,
  isAdmin = false,
  onEditItem,      // (unit) => void — opens KnowledgeDetailModal in parent
  onItemUpdated,   // () => void — refresh after approve/reject
}) {
  const [busyId, setBusyId] = useState(null);
  // Vincent XLSX 2026-07-07 22h Row #13-14 (Eric 2026-07-08 SPEC STRICK) —
  // Optimistic status cache pour badges Acceptée/Refusée instantanés.
  const [optimisticStatus, setOptimisticStatus] = useState({});
  // Vincent B614 (Eric 2026-07-29 SPEC STRICK) — Set des KUs favorites de
  // l'utilisateur pour afficher le ♥ sur chaque concept.
  const favKuIds = useFavoriteKuIds();

  // Vincent #147 (Eric 2026-05-21, round 2) — Cet onglet est désormais nommé
  // "Concepts" et doit afficher UNIQUEMENT les concepts financiers extraits.
  // Les procédures, Q&R, scripts et objections ont chacun leur propre onglet
  // dédié — les remontrer ici serait une duplication désorientante.
  const filteredUnits = (knowledgeUnits || []).filter(
    u => u.type === "concept"
  );

  const reviewItem = async (unit, action) => {
    if (busyId) return;
    if (action === "reject") {
      const ok = await confirmDialog(
        lang === "fr"
          ? "Refuser cette connaissance ? Elle sera masquée mais conservée pour audit (Loi 25)."
          : "Reject this knowledge? It will be hidden but kept for audit."
      );
      if (!ok) return;
    }
    setBusyId(unit.id);
    // Vincent XLSX 2026-07-07 22h Row #13-14 (Eric 2026-07-08 SPEC STRICK) —
    // Optimistic UI update : le badge « Acceptée » (vert) / « Refusée » (rouge)
    // apparaît INSTANTANÉMENT au clic, sans attendre le round-trip parent.
    // On patche le unit local (le prop reste immutable, mais on stocke le
    // nouveau status dans `optimisticStatus` qui est prioritaire à l'affichage).
    setOptimisticStatus((prev) => ({ ...prev, [unit.id]: action === "approve" ? "approved" : "rejected" }));
    try {
      await axios.put(
        `${API}/api/knowledge/${unit.id}/review`,
        { action },
        { withCredentials: true }
      );
      toast.success(action === "approve"
        ? (lang === "fr" ? "Connaissance acceptée" : "Knowledge approved")
        : (lang === "fr" ? "Connaissance refusée" : "Knowledge rejected")
      );
      onItemUpdated?.();
    } catch (err) {
      // Rollback optimistic update on error
      setOptimisticStatus((prev) => {
        const next = { ...prev };
        delete next[unit.id];
        return next;
      });
      toast.error(err.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="max-h-[calc(100vh-280px)] min-h-[300px] overflow-y-auto"
      data-testid="knowledge-container"
    >
      {filteredUnits.length > 0 ? (
        <div className="p-3 space-y-2">
          {filteredUnits.map((unit) => {
            const Icon = typeIcons[unit.type] || Lightbulb;
            const colors = typeColors[unit.type] || typeColors.concept || { bg: "#f4f4f5", border: "#e4e4e7", color: "#71717a" };
            const effectiveStatus = optimisticStatus[unit.id] || unit.status;
            const isPending = effectiveStatus === "pending_review";
            const isApproved = effectiveStatus === "approved";
            const isRejected = effectiveStatus === "rejected";
            const isBusy = busyId === unit.id;
            return (
              <div
                key={unit.id}
                className="p-3 rounded-lg border transition-all duration-200 hover:shadow-md"
                style={{ backgroundColor: colors.bg, borderColor: colors.border }}
                data-testid={`knowledge-item-${unit.id}`}
                data-focus-target={unit.id}
              >
                <div className="flex items-start gap-2.5">
                  {/* Vincent B614 (Eric 2026-07-29 SPEC STRICK) — ♥ favori
                      pour chaque connaissance (concept, objection, …). */}
                  <KUFavoriteHeart
                    unitId={unit.id}
                    initialIsFavorite={favKuIds.has(unit.id)}
                    testIdPrefix="fav-knowledge-btn"
                    className="mt-0.5"
                  />
                  <button
                    type="button"
                    onClick={() => unit.timestamps?.start != null && seekTo(unit.timestamps.start)}
                    className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${colors.color}20` }}
                    data-testid={`knowledge-seek-${unit.id}`}
                    title={unit.timestamps?.start != null ? (lang === "fr" ? "Aller à ce moment" : "Jump here") : ""}
                  >
                    <Icon size={14} style={{ color: colors.color }} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider"
                        style={{ color: colors.color }}
                      >
                        {unit.type}
                      </span>
                      {unit.timestamps?.start != null && (
                        <button
                          type="button"
                          onClick={() => seekTo(unit.timestamps.start)}
                          className="text-[10px] text-zinc-500 font-mono hover:text-[#002FA7] hover:underline"
                          data-testid={`knowledge-ts-${unit.id}`}
                        >
                          {formatTime(unit.timestamps.start)}
                        </button>
                      )}
                      {/* Status badge — Vincent #146 */}
                      {isPending && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200" data-testid={`knowledge-status-pending-${unit.id}`}>
                          {lang === "fr" ? "EN RÉVISION" : "REVIEW"}
                        </span>
                      )}
                      {isApproved && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 border border-teal-200" data-testid={`knowledge-status-approved-${unit.id}`}>
                          {lang === "fr" ? "VALIDÉE" : "APPROVED"}
                        </span>
                      )}
                      {isRejected && (
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-800 border border-red-200" data-testid={`knowledge-status-rejected-${unit.id}`}>
                          {lang === "fr" ? "REFUSÉE" : "REJECTED"}
                        </span>
                      )}
                    </div>
                    <h4
                      className="text-sm font-medium text-zinc-900 line-clamp-2 leading-snug cursor-pointer"
                      onClick={() => onEditItem?.(unit)}
                    >
                      {unit.title || (lang === "fr" ? "Sans titre" : "Untitled")}
                    </h4>
                    {unit.content?.definition && (
                      <p className="text-xs text-zinc-500 mt-1 line-clamp-2 leading-relaxed">{unit.content.definition}</p>
                    )}
                    {unit.content?.question && (
                      <p className="text-xs text-zinc-500 mt-1 line-clamp-2 leading-relaxed">Q: {unit.content.question}</p>
                    )}
                  </div>
                  {/* Vincent #146 — Boutons Accepter / Éditer / Refuser pour admins */}
                  {isAdmin && (
                    <div className="flex flex-col gap-1 shrink-0">
                      {onEditItem && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onEditItem(unit); }}
                          disabled={isBusy}
                          className="p-1.5 rounded hover:bg-[#002FA7]/10 text-[#002FA7] disabled:opacity-50"
                          title={lang === "fr" ? "Éditer" : "Edit"}
                          data-testid={`knowledge-edit-${unit.id}`}
                        >
                          <PencilSimple size={13} weight="bold" />
                        </button>
                      )}
                      {isPending && (
                        <>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); reviewItem(unit, "approve"); }}
                            disabled={isBusy}
                            className="p-1.5 rounded hover:bg-teal-50 text-teal-600 disabled:opacity-50"
                            title={lang === "fr" ? "Accepter" : "Approve"}
                            data-testid={`knowledge-approve-${unit.id}`}
                          >
                            {isBusy ? <SpinnerGap size={13} className="animate-spin" /> : <Check size={13} weight="bold" />}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); reviewItem(unit, "reject"); }}
                            disabled={isBusy}
                            className="p-1.5 rounded hover:bg-red-50 text-red-600 disabled:opacity-50"
                            title={lang === "fr" ? "Refuser" : "Reject"}
                            data-testid={`knowledge-reject-${unit.id}`}
                          >
                            <X size={13} weight="bold" />
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="p-10 text-center text-zinc-400">
          <Brain size={36} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">
            {isProcessing
              ? (lang === "fr" ? "Extraction en cours…" : "Extraction in progress…")
              : noKnowledgeYetLabel}
          </p>
        </div>
      )}
    </div>
  );
}
